import React, { useState, useEffect } from 'react';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import ImageViewer from '../../components/ImageViewer/index';
import LoadingScreen from '../../components/LoadingScreen';
import { loadDicomImage, organizeRecords, loadZipFromUrl } from '../../services/DicomService';
import { DicomViewerContainer, ViewerContent, ViewerContainer } from './styles';

function DicomdirViewer() {
  const [records, setRecords] = useState([]);
  const [organizedRecords, setOrganizedRecords] = useState({});
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [zipFiles, setZipFiles] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(1);
  const [currentSeries, setCurrentSeries] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(0);

  useEffect(() => {
    cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
    cornerstoneWADOImageLoader.configure({
      beforeSend: (xhr) => {
        // Additional configurations if needed
      },
    });

    // Load ZIP file from URL query parameter
    const urlParams = new URLSearchParams(window.location.search);

    // Pegar o valor zipUrl diretamente da URL para evitar problemas com espaços
    const getZipUrlFromSearch = () => {
      const search = window.location.search;
      const match = search.match(/[?&]zipUrl=([^&]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    };

    const encodedZipUrl = getZipUrlFromSearch();
    console.log('Direct zipUrl from URL:', encodedZipUrl);
    const enableMock = urlParams.get('enableMock');
    let zipUrl = null;

    try {
      if (encodedZipUrl) {
        try {
          zipUrl = atob(encodedZipUrl);
          console.log('Successfully decoded base64 URL');
        } catch (decodeError) {
          console.error('Error decoding ZIP URL:', decodeError);
          throw new Error('Invalid URL: base64 decoding failed');
        }
      } else if (enableMock) {
        console.log('Using mock URL for development');
        zipUrl = 'https://dh3h7m23hh6gv.cloudfront.net/Teste_dicon%2FASAS%20-%20Cervical%20spine%20MRI.zip?Expires=1767901989&Key-Pair-Id=APKAIJXUEEYXUJ4EAJXQ&Signature=JPSo1N35ttQfHgdC-yHrsTiVRCVJFaJtCB3~d3p3XAD3K6kT1VEw9X3s1W25HpCxSn17-EeHT5OUY6NXhokVE-95D1robzDkDgvQ8BwJqlx7M7-Gl9JwVuWPE-q7pQ64jW40cjjSbFwHkWFHHl3yPdRtQk79Nx-g85874-zvPsuHEYiwQzUJsVvALJFFfVyeM7mrPlv7fH-IqBuMUAEAMu4C3QzvqHtOpkS6VsqDVJ4NJK37HPxO41qeEP4LpjMTdc1RHCc~1nKCZKTYSQxtTqCSM4LgTeSyQNdBNDrfd~fwJa77XDjI~dPO~YYyGkZ-dO-NDZKmqw-CtKE0A61pcA__';
      }

      if (!zipUrl) {
        throw new Error('No valid URL provided. Use zipUrl parameter or enable mock mode');
      }

      try {
        const urlObject = new URL(zipUrl);
        console.log('URL object:', urlObject, zipUrl);
        if (!urlObject.protocol.startsWith('http')) {
          throw new Error('Invalid URL protocol - URL must start with http:// or https://');
        }

        loadZipFromUrl(zipUrl, setLoading, setError, setRecords, setZipFiles, setDownloadProgress);
      } catch (error) {
        console.error('Error processing ZIP URL:', error);
        setError(`Invalid ZIP URL provided: ${error.message}`);
        setLoading(false);
      }
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  }, []);

  // Update organized records when records change
  useEffect(() => {
    if (records && records.length > 0) {
      const organized = organizeRecords(records);
      setOrganizedRecords(organized);
    }
  }, [records]);

  // Auto-select first study when organized records are available
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const autoSelectFirstImage = async () => {
      // Ensure we have organized records and are not already loading an image
      if (!organizedRecords || Object.keys(organizedRecords).length === 0) {
        return;
      }

      try {
        const firstSeriesId = Object.keys(organizedRecords)[0];
        const firstSeries = organizedRecords[firstSeriesId];

        if (firstSeries?.images?.length > 0) {
          const firstImage = firstSeries.images[0];

          // Load the image
          const loadedImage = await loadDicomImage(
            firstImage.referencedFile,
            zipFiles,
            loadedImages,
            setLoadedImages
          );

          if (loadedImage) {
            // Update states immediately
            setCurrentSeries(firstSeries);
            setSelectedRecord(firstImage);
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    };

    // Attempt to auto-select as soon as possible
    if (loading && organizedRecords && Object.keys(organizedRecords).length > 0) {
      autoSelectFirstImage();
    }
  }, [loading, organizedRecords, zipFiles]);

  // Add a new useEffect to ensure first series is selected if not already done
  useEffect(() => {
    if (!currentSeries && organizedRecords && Object.keys(organizedRecords).length > 0) {
      const firstSeriesId = Object.keys(organizedRecords)[0];
      const firstSeries = organizedRecords[firstSeriesId];

      if (firstSeries?.images?.length > 0) {
        const firstImage = firstSeries.images[0];

        setCurrentSeries(firstSeries);
        setSelectedRecord(firstImage);
      }
    }
  }, [organizedRecords, currentSeries]);

  const handleSeriesSelect = async (series, firstImage) => {
    setCurrentSeries(series);
    setSelectedRecord(firstImage);
  };

  return (
    <DicomViewerContainer>
      {loading && <LoadingScreen progress={downloadProgress} />}
      <ViewerContent>
        <ViewerContainer>
          <ImageViewer
            selectedRecord={selectedRecord}
            brightness={brightness}
            contrast={contrast}
            loadDicomImage={loadDicomImage}
            zipFiles={zipFiles}
            loadedImages={loadedImages}
            setLoadedImages={setLoadedImages}
            setBrightness={setBrightness}
            setContrast={setContrast}
            loading={loading}
            error={error}
            records={records}
            onSeriesSelect={handleSeriesSelect}
            organizedRecords={organizedRecords}
            currentSeries={currentSeries}
            onImageSelect={setSelectedRecord}
          />
        </ViewerContainer>
      </ViewerContent>
    </DicomViewerContainer>
  );
}

export default DicomdirViewer;
